import { services } from '@config/services';
import { integrationApi } from './_base';

/**
 * Merchant migration endpoints on the integration service's plugin resource
 * (auth `Plugins.ManageConnections`). Bodies and responses are camelCase with
 * no envelope:
 *
 *   POST /v1/plugin/{id}/merchantPlugins/migrationPreview
 *     { merchantPluginIds, targetPluginVersionId }
 *     → { targetPluginVersionId, targetPluginVersionName, merchants: [...] }
 *   POST /v1/plugin/{id}/merchantPlugins/migrate
 *     { batchId, targetPluginVersionId, merchantPlugins: [{ merchantPluginId, variableValues, reviewMappings }] }
 *     → { batchId, targetPluginVersionId, targetVersionName, alreadyProcessed, succeeded, failed, skipped }
 *   POST /v1/plugin/{id}/merchantPlugins/confirmMappingReview
 *     { batchId?, merchantPluginIds }
 *     → { batchId, confirmed, notPending }
 */

export interface MigrationPreviewArgs {
  pluginId: string;
  merchantPluginIds: string[];
  targetPluginVersionId: string;
}

export interface MigrationPreviewMerchant {
  merchantPluginId: string;
  merchantId: string;
  merchantName: string;
  currentPluginVersionId: string;
  currentPluginVersionName: string;
  /** Mandatory on the target, not declared by the current version. */
  newRequiredKeys: string[];
  /** Mandatory on the target, declared today but blank. */
  blankRequiredKeys: string[];
  /** Values the merchant set for keys the current version no longer declares. */
  retainedValues: Record<string, string>;
  hasShippingOverride: boolean;
  hasProductOverride: boolean;
  mappingReviewPending: boolean;
}

export interface MigrationPreviewResponse {
  targetPluginVersionId: string;
  targetPluginVersionName: string;
  merchants: MigrationPreviewMerchant[];
}

export interface MigrateMerchantPluginInput {
  merchantPluginId: string;
  variableValues: Record<string, string> | null;
  reviewMappings: boolean;
}

export interface MigrateMerchantPluginsArgs {
  pluginId: string;
  /** Client-generated; a replay of the same id returns the recorded result. */
  batchId: string;
  targetPluginVersionId: string;
  merchantPlugins: MigrateMerchantPluginInput[];
}

export interface MigratedMerchant {
  merchantPluginId: string;
  merchantId: string;
  merchantName: string;
  fromVersionId: string | null;
  fromVersionName: string | null;
  /** Human-readable reason, set on `failed` entries. */
  error: string | null;
}

export interface MigrateMerchantPluginsResponse {
  batchId: string;
  targetPluginVersionId: string | null;
  targetVersionName: string | null;
  /** True when the batch id was already processed and this is the recorded result. */
  alreadyProcessed: boolean;
  succeeded: MigratedMerchant[];
  failed: MigratedMerchant[];
  /** Already on the target version; left untouched. */
  skipped: MigratedMerchant[];
}

export interface ConfirmMappingReviewArgs {
  pluginId: string;
  batchId?: string;
  merchantPluginIds: string[];
}

export interface ConfirmMappingReviewResponse {
  batchId: string | null;
  confirmed: string[];
  notPending: string[];
}

const pluginPath = (pluginId: string): string =>
  `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}`;

export const merchantMigrationRequestBuilders = {
  migrationPreview: ({ pluginId, merchantPluginIds, targetPluginVersionId }: MigrationPreviewArgs) => ({
    url: `${pluginPath(pluginId)}/merchantPlugins/migrationPreview`,
    method: 'POST',
    body: { merchantPluginIds, targetPluginVersionId },
  }),
  migrateMerchantPlugins: ({
    pluginId,
    batchId,
    targetPluginVersionId,
    merchantPlugins,
  }: MigrateMerchantPluginsArgs) => ({
    url: `${pluginPath(pluginId)}/merchantPlugins/migrate`,
    method: 'POST',
    body: { batchId, targetPluginVersionId, merchantPlugins },
  }),
  confirmMappingReview: ({ pluginId, batchId, merchantPluginIds }: ConfirmMappingReviewArgs) => ({
    url: `${pluginPath(pluginId)}/merchantPlugins/confirmMappingReview`,
    method: 'POST',
    body: batchId === undefined ? { merchantPluginIds } : { batchId, merchantPluginIds },
  }),
};

const list = <T>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);

/**
 * Fail open per merchant: the page indexes these arrays and the map directly,
 * so a missing one must read as "nothing to fill", not crash the form.
 */
export function transformMigrationPreviewResponse(
  response: Partial<MigrationPreviewResponse> | null | undefined,
): MigrationPreviewResponse {
  return {
    targetPluginVersionId: response?.targetPluginVersionId ?? '',
    targetPluginVersionName: response?.targetPluginVersionName ?? '',
    merchants: list(response?.merchants).map((m) => ({
      ...m,
      newRequiredKeys: list(m.newRequiredKeys),
      blankRequiredKeys: list(m.blankRequiredKeys),
      retainedValues: m.retainedValues ?? {},
      hasShippingOverride: m.hasShippingOverride === true,
      hasProductOverride: m.hasProductOverride === true,
      mappingReviewPending: m.mappingReviewPending === true,
    })),
  };
}

export function transformMigrateMerchantPluginsResponse(
  response: Partial<MigrateMerchantPluginsResponse> | null | undefined,
): MigrateMerchantPluginsResponse {
  return {
    batchId: response?.batchId ?? '',
    targetPluginVersionId: response?.targetPluginVersionId ?? null,
    targetVersionName: response?.targetVersionName ?? null,
    alreadyProcessed: response?.alreadyProcessed === true,
    succeeded: list(response?.succeeded),
    failed: list(response?.failed),
    skipped: list(response?.skipped),
  };
}

export function transformConfirmMappingReviewResponse(
  response: Partial<ConfirmMappingReviewResponse> | null | undefined,
): ConfirmMappingReviewResponse {
  return {
    batchId: response?.batchId ?? null,
    confirmed: list(response?.confirmed),
    notPending: list(response?.notPending),
  };
}

const merchantMigrationApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Read-only but POST (the id list rides the body). Never served from cache.
     * Its own tag, not invalidated by migrate: a refetch mid-flow would unmount
     * the form and the results dialog.
     */
    migrationPreview: builder.query<MigrationPreviewResponse, MigrationPreviewArgs>({
      query: (args) => merchantMigrationRequestBuilders.migrationPreview(args),
      transformResponse: transformMigrationPreviewResponse,
      providesTags: (_r, _e, { pluginId }) => [{ type: 'MigrationPreview', id: pluginId }],
      keepUnusedDataFor: 0,
    }),
    migrateMerchantPlugins: builder.mutation<MigrateMerchantPluginsResponse, MigrateMerchantPluginsArgs>({
      query: (args) => merchantMigrationRequestBuilders.migrateMerchantPlugins(args),
      transformResponse: transformMigrateMerchantPluginsResponse,
      invalidatesTags: (_r, _e, { pluginId, merchantPlugins }) => [
        { type: 'VersionDistribution', id: pluginId },
        { type: 'PluginConnections', id: pluginId },
        { type: 'MerchantPlugins', id: 'LIST' },
        ...merchantPlugins.map((m) => ({ type: 'MerchantPluginDetail' as const, id: m.merchantPluginId })),
      ],
    }),
    confirmMappingReview: builder.mutation<ConfirmMappingReviewResponse, ConfirmMappingReviewArgs>({
      query: (args) => merchantMigrationRequestBuilders.confirmMappingReview(args),
      transformResponse: transformConfirmMappingReviewResponse,
      invalidatesTags: (_r, _e, { pluginId }) => [{ type: 'PluginConnections', id: pluginId }],
    }),
  }),
});

export const {
  useMigrationPreviewQuery,
  useMigrateMerchantPluginsMutation,
  useConfirmMappingReviewMutation,
} = merchantMigrationApi;
